import { format } from 'date-fns';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { EmployeesRole, ShortDay } from '../../../types/employees';
import useOnschedResource from '../../../hooks/employees/useOnschedResource';
import { Employee } from '../../../interfaces';
import {
  CreateArtistValues,
  initFormValues,
} from '../../../utils/form/artistForm';
import {
  convertOnschedHoursToDate,
  convertShortDayToFull,
} from '../../../utils/global/helper';
import ArtistHoursInputsModal from '../../ArtistHoursInputsModal';
import { orderAvalabilityDays } from '../../../helpers/employees';
import EditableInfoSection from '../EditableInfoSection';

interface ArtistHoursManagementProps {
  employee?: Employee | null;
}

function ArtistHoursInputGroup({ employee }: ArtistHoursManagementProps) {
  const [open, setOpen] = useState(false);
  const { values, setFieldValue } = useFormikContext<CreateArtistValues>();

  const { data, isFetching } = useOnschedResource(employee?.employeeId || '', {
    enabled: !!employee?.employeeId,
  });
  const isLoading =
    !!employee?.employeeId &&
    (isFetching || data?.id !== employee?.onschedResourceId);

  const { availability = initFormValues.weeklyAvailability } = data || {};

  const memoizedAvailability = useMemo(() => availability, [availability]);

  const onEditHours = () => setOpen(true);

  // keep formik values in sync with onsched data
  useEffect(() => {
    if (memoizedAvailability && employee?.employeeId) {
      setFieldValue('weeklyAvailability', memoizedAvailability);
    }
  }, [memoizedAvailability, setFieldValue, employee?.employeeId]);

  const isBookable =
    values.isBookable || values.selectedRoles.includes(EmployeesRole.PIERCER);

  return (
    <>
      <EditableInfoSection
        title="Hours"
        button={{
          text: 'Edit Hours',
          onClick: onEditHours,
          disabled: isLoading || !isBookable,
          tooltip: !isBookable ? 'This artist is not bookable' : undefined,
        }}
        rows={orderAvalabilityDays(Object.keys(availability)).map(day => {
          const dayData =
            values.weeklyAvailability[
              day as keyof typeof values.weeklyAvailability
            ];
          const start = format(
            convertOnschedHoursToDate(dayData.startTime),
            'h:mm a',
          );
          const end = format(
            convertOnschedHoursToDate(dayData.endTime),
            'h:mm a',
          );
          const isClosed = dayData.startTime === 0 && dayData.endTime === 0;
          return {
            title: convertShortDayToFull(day as ShortDay),
            value: isClosed ? 'Closed' : `${start} - ${end}`,
          };
        })}
        isLoading={isLoading}
      />

      {open && (
        <ArtistHoursInputsModal
          open={open}
          onClose={() => setOpen(false)}
          availability={values.weeklyAvailability}
        />
      )}
    </>
  );
}

export default ArtistHoursInputGroup;
